define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"csi",
	"csi/_base/underscore",
	"csi/def",
	"csi/def/Action",
	"csi/def/Field",
	"csi/def/Relationship",
	"csi/def/ViewRef",
	"csi/form/Widget",
	"csi/store/Names",
	"csi/store/_base",
	"csi/xml",
	"csi/xml/Search"
], function(declare, lang, csi, _, def, Action, Field, Relationship, ViewRef, Widget, Names, csiStore, xml, Search) {

	const COLLECTION = "Collection";
	const PRODUCT_TYPE = "ProductType";
	const PRODUCT_TEMPLATE = "Template";
	const PARENT_SEASON = "ParentSeason";
	const STYLE = "Style";
	const THEME = "Theme";
	const SHAPE = "Shape";

	function _getTemplateStore(type, ctx) {
		// create store for templates based on selected type
		var search = new Search();
		var q = search.getQuery();

		q.Attr("IsTemplate", xml.OP_EQUALS, true)
			.Attr("Active", xml.OP_EQUALS, true)
			.Attr("IsInspiration", xml.OP_EQUALS, false);

		if (xml.isValidURL(type)) {
			q.RefAttr(PRODUCT_TYPE, xml.OP_EQUALS, type);
		} else {
			q.Attr("Available", xml.OP_EQUALS, true, "Child:ProductType/Child:ProductionConfig")
				.Attr("Published", xml.OP_EQUALS, true, "Child:ProductType");
		}

		var rel = lang.mixin(new Relationship(), {m_typeNode: STYLE, m_query: q});
		var storeOptions = csiStore.namesOptionsByRelationship(ctx, rel);
		storeOptions.data = [csiStore.nameObject("")];
		storeOptions.action = lang.mixin(new Action(), {
			m_viewRef: lang.mixin(new ViewRef(), {m_id: "StyleTemplates", m_url: "centric://APPAREL/Views"}),
			m_fixedQuery: q
		});

		return new Names(storeOptions);
	}

	return declare(Widget, {
		/** @property {boolean} isShapeOrThemeContext - flag to determine when the form is being created from shape or theme */
		isShapeOrThemeContext: false,

		bAddHierarchyFields: false,

		/** @property {cnl} previousSeasonUrlForShape - url of season when aggregate store for Shape was created */
		previousSeasonUrlForShape: null,

		/** @property {cnl} previousSeasonUrlForTheme - url of season when aggregate store for Theme was created */
		previousSeasonUrlForTheme: null,

		/** @property {String} productSourceCreationLevel - name of attribute on Supplier Request to store the creation level of the Style */
		productSourceCreationLevel: null,

		PRODUCT_SOURCE_CREATION_LEVEL: "ProductSourceCreationLevel",

		getFieldStore: function(/*Field*/field, /*String*/value) {
			// summary: if field is Template, then return store based on product type
			var ctx = this.getContext();
			const {defView} = this;

			if (field.getControlName() == PRODUCT_TEMPLATE) {
				return _getTemplateStore(defView.getFieldValue(PRODUCT_TYPE), ctx);
			}

			return this.inherited(arguments);
		},

		/**
		 * @override
		 * Track season URL when report was generated for Theme or Shape field (should be same as both are updated at the same time)
		 */
		onWriteFormReport(field, writer, formInfo) {
			if (field.m_id === SHAPE) {
				this.previousSeasonUrlForShape = this.getFieldValue(PARENT_SEASON);
			} else if (field.m_id === THEME) {
				this.previousSeasonUrlForTheme = this.getFieldValue(PARENT_SEASON);
			}

			// if Style is created from Supplier Request, then set fake/temporary ProductSourceCreationLevel attribute to store the creation level
			if (this.productSourceCreationLevel) {
				const {nodeUrl} = formInfo;
				writer.changeNode(nodeUrl)
					.publishAttribute(this.PRODUCT_SOURCE_CREATION_LEVEL, xml.ATTR_STRING, this.productSourceCreationLevel);
			}
		},

		/**
		 * @override
		 * When template field is queried, set disabled status based on number of results
		 */
		onSetDefaultValue: function(field, store, value) {
			if (field.m_id === SHAPE || field.m_id === THEME) {
				// if the field is shape or theme and the new style is beign created from shape or theme we dont need to set the default value
				if (this.isShapeOrThemeContext) {
					return;
				}
			}
			this.inherited(arguments);

			if (field.getControlName() == PRODUCT_TEMPLATE) {
				var urlType = this.getFieldValue(PRODUCT_TYPE);
				var total = store.internalQuery(null, {useCache: true}).length;
				this.setFieldDisabled(field, xml.isValidURL(urlType) && total <= 1);
			}
		},

		_addHierarchyField: function(/*String*/id) {
			// summary: add field to pick hierarchy above Style
			this.insertField(lang.mixin(new Field(), {
				m_id: id,
				bForceEditable: true,
				m_bRequired: true,
				bDisableExpressionEvaluation: true //C8-2841: disable one-time expressions on hierarchy attributes, since should be selected in the form
			}), 0);
		},

		/**
		 * @override
		 * If Collection field is on the form and has a value, then return it.
		 */
		_getParentURLOfClientNode() {
			const url = this.getFieldValue(COLLECTION);
			return xml.isValidURL(url) ? url : this.inherited(arguments);
		},

		/**
		 * @override
		 * A Style's parent will always be a Collection
		 */
		_getParentTypeOfClientNode() {
			return COLLECTION;
		},

		_resolveView: function() {
			// summary: add Style Template field if there are templates and add behavior to ProductType
			if (this.bAddHierarchyFields) {
				this._addHierarchyField(COLLECTION);
				const f = this.defView.getField(COLLECTION);
				f.m_onChange = "_onChangeCollection";
				this._addHierarchyField("Category2");
				this._addHierarchyField("Category1");
				this._addHierarchyField(PARENT_SEASON);
			}

			// prevent drawing of Template field in Style Edit window.
			if (!this.isEdit()) {
				this.insertField(lang.mixin(new Field(), {
					m_bInitialize: true,
					m_dependency: PRODUCT_TYPE,
					m_label: PRODUCT_TEMPLATE,
					m_typeControl: def.FIELD_FILTERING_SELECT,
					m_onChange: "_onChangeTemplate"
				}), 0);
			}

			this.insertField(lang.mixin(new Field(), {m_id: PRODUCT_TYPE}), 0);

			// determine if there is an initial value for Style
			var fd = this.defView.m_formData;
			if (fd) {
				var ac = fd.getSource();
				var sr = ac.getParent();

				// if permissions deny access to Style Viewer, try to get the Style Type from the Inspiration Style Type of the Supplier Request
				var url = ac._resolveStyleType && (ac._resolveStyleType(sr) || ac._resolveStyleType(fd.getNode())) || sr.InspirationStyleType;
				if (url) {
					this.addDefaultValue(PRODUCT_TYPE, url);
				}
			}

			var shape = this.defView.getField(SHAPE);
			if (shape) {
				shape.m_dependency = COLLECTION;
			}

			var theme = this.defView.getField(THEME);
			if (theme) {
				theme.m_dependency = COLLECTION;
			}

			this.inherited(arguments);
		},

		_onChangeTemplate: function(defView, field, value) {
			// summary: fill in style type if not selected based on template type
			var fType = this.defView.getField(PRODUCT_TYPE);
			var template = this.cacheView.getNode(value, true);

			if (template && (fType.getValue() == csi.EMPTY_URL)) {
				this.setFieldValue(fType, template.ProductType, true);
				this.setFieldStore(field, _getTemplateStore(template.ProductType, this.getContext()));
			}

			// update values on other fields
			const values = this._persistFormValues = {};

			this.defView.m_fields.forEach((field) => {
				const id = field.m_id;
				const value = template ? template[id] : field.m_rca?.getValue();

				if (id && id != xml.ATTR_NAME && id != PRODUCT_TYPE && xml.isValidURL(value)) {
					values[field.getUniqueControlName()] = value;
				}
			});

			this._setPersistFieldsValue().then(() => {
				this._persistFormValues = null;
			});
		},

		/**
		 * @override
		 * No need to update Shape and Theme fields if season did not change. We track the dependency at the Collection
		 * level though because can't get from Style to Season without the Collection
		 */
		_onDependency(field, fieldSource) {
			const {previousSeasonUrlForShape, previousSeasonUrlForTheme} = this;
			const seasonUrl = this.getFieldValue(PARENT_SEASON);
			const {m_id} = field;

			if (((m_id === SHAPE) && (seasonUrl === previousSeasonUrlForShape)) ||
				((m_id === THEME) && (seasonUrl === previousSeasonUrlForTheme))) {
				return;
			}

			this.inherited(arguments);
		},

		_onChangeCollection: function(defView, field, value) {
			const formNode = this.getFormNode();
			formNode.__Parent__ = value;
		}
	});
});
